import { Flex, Space, Text, TextVariant, useRadioGroup } from '@billy/design-system'
import uniqueId from 'lodash/uniqueId'
import { ChangeEvent, ReactElement, useCallback, useEffect, useMemo, useState } from 'react'

import { getImageClassName } from '../../utils/getClassName'
import * as Styled from './styles'

export type LabelVariant = TextVariant

export interface RadioButtonSectionProps<T = string> {
  checked?: boolean
  description?: string
  label: string
  labelVariant?: LabelVariant
  onChange: (value?: T) => void
  value: T
}

export const RadioButtonSection = <T extends string>({
  checked,
  description,
  label,
  labelVariant = 'h3',
  onChange,
  value,
}: RadioButtonSectionProps<T>): ReactElement => {
  const { name: groupName, value: groupValue, onChange: onGroupChange } = useRadioGroup()
  const groupValueTyped = groupValue as T
  const [checkedValue, setCheckedValue] = useState<boolean | undefined>(checked || groupValueTyped === value)

  const id = useMemo(() => `${uniqueId('radio-')}-${value}`, [value])
  const radioGroupName = useMemo(() => `group-${groupName}`, [groupName])

  const handleValueChange = useCallback((newCheckedValue: boolean) => {
    setCheckedValue(newCheckedValue)
  }, [])

  const handleRadioChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCheckedValue(event.currentTarget.checked)
      onGroupChange?.(event)
    },
    [onGroupChange],
  )

  useEffect(() => {
    if (checked !== undefined) {
      handleValueChange(checked)
    }
  }, [checked, handleValueChange])

  useEffect(() => {
    if (groupValueTyped) {
      handleValueChange(groupValueTyped === value)
      onChange(groupValueTyped)
    }
  }, [groupValue, groupValueTyped, handleValueChange, onChange, value])

  return (
    <Styled.RadioButtonSectionWrapper htmlFor={id} checked={checkedValue}>
      <Styled.LeftSection>
        <Styled.RadioControl checked={checkedValue} className={getImageClassName()} />
      </Styled.LeftSection>
      <Flex flexDirection="column" justifyContent="center">
        <Styled.NativeRadio
          checked={checkedValue}
          id={id}
          name={radioGroupName}
          onChange={handleRadioChange}
          type="radio"
          value={value}
        />
        <Text variant={labelVariant}>{label}</Text>
        {description && (
          <>
            <Space size="s8" />
            <Text variant="bodySM">{description}</Text>
          </>
        )}
      </Flex>
    </Styled.RadioButtonSectionWrapper>
  )
}
